import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Crown } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";

export default function CheckoutPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get("plan") === "monthly" ? "monthly" : "annual";
  const { me, loaded: meLoaded } = useAuth();
  const [promo, setPromo] = useState("");
  const [waiver, setWaiver] = useState(false); // §18: unticked by default, collected once, at payment, for an identified user
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [profileMissing, setProfileMissing] = useState(false);

  useEffect(() => {
    // Payer suppose un compte : un visiteur confirmé anonyme part s'identifier.
    if (meLoaded && !me) navigate(`/auth?next=${encodeURIComponent(`/checkout?plan=${plan}`)}`);
  }, [meLoaded, me, navigate, plan]);

  const pay = async () => {
    setBusy(true);
    setError("");
    setProfileMissing(false);
    try {
      const r = await api.post("/billing/checkout", {
        origin_url: window.location.origin, promo_code: promo.trim() || null, plan,
        waiver_accepted: waiver, waiver_lang: lang,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "invalid_promo") setError(t("premium.invalid_promo"));
      else if (detail === "profile_incomplete") setProfileMissing(true);
      else if (detail === "waiver_required") setError(t("premium.waiver_required"));
      else setError(formatApiError(e));
      setBusy(false);
    }
  };

  if (!me) return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;
  if (me.role === "admin" || me.plan === "premium") {
    return (
      <div className="mx-auto max-w-md pt-16 text-center" data-testid="checkout-already-premium">
        <p className="font-mono2 text-xs uppercase tracking-wider text-[#00FF87]">{t("premium.current_plan")}</p>
        <Link to="/dashboard" className="mt-4 inline-block font-mono2 text-[11px] uppercase tracking-wider text-[#00F0FF] hover:underline">{t("nav.dashboard")} →</Link>
      </div>
    );
  }

  const inputCls = "w-full rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2.5 text-sm text-slate-200 focus:border-[#FFD600] focus:outline-none";
  return (
    <div className="mx-auto max-w-md pt-10" data-testid="checkout-page">
      <h1 className="font-display text-3xl font-black uppercase tracking-tight text-slate-50">{t("checkout.title")}</h1>
      <p className="mt-1 font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid="checkout-account">{me.email}</p>

      <section className="mt-6 rounded-xl border border-[#FFD600]/60 bg-[#141A26] p-5" data-testid="checkout-summary">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#FFD600]"><Crown className="h-3 w-3" /> Premium · {t(`premium.plan_${plan}`)}</span>
          <span className="font-mono2 text-base font-bold text-slate-100" data-testid="checkout-price">
            {plan === "monthly" ? t("premium.monthly_price") : t("premium.annual_price")}
            <span className="ml-1 text-[10px] font-normal text-slate-500">{plan === "monthly" ? t("premium.per_month") : t("premium.per_year")}</span>
          </span>
        </div>
        <p className="mt-1 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("premium.vat_included")}</p>
        <Link to="/pricing" className="mt-2 inline-block font-mono2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-[#00F0FF]" data-testid="checkout-change-plan">{t("checkout.change_plan")}</Link>
      </section>

      <section className="mt-6 space-y-4" data-testid="checkout-form">
        <input data-testid="promo-code-input" className={`${inputCls} font-mono2 text-xs uppercase tracking-wider`} placeholder={t("premium.promo_placeholder")} value={promo} onChange={(e) => setPromo(e.target.value)} />
        <label className="flex items-start gap-2 text-left text-[11px] leading-relaxed text-slate-400" data-testid="waiver-block">
          <input type="checkbox" data-testid="waiver-checkbox" checked={waiver} onChange={(e) => setWaiver(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#FFD600]" />
          <span>{t("premium.waiver_label")} <Link to="/legal/cgv" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline" data-testid="waiver-cgv-link">{t("premium.waiver_link")}</Link></span>
        </label>
        <p className="font-mono2 text-[9px] uppercase tracking-wider text-slate-600">{t("checkout.consent_note")}</p>
        {error && <p className="text-xs text-red-400" data-testid="checkout-error">{error}</p>}
        {profileMissing && (
          <p className="rounded-md border border-[#FFD600]/40 bg-[#FFD600]/5 px-3 py-2 text-xs text-[#FFD600]" data-testid="profile-incomplete-msg">
            {t("premium.profile_incomplete")} <Link to="/account" target="_blank" rel="noopener noreferrer" className="font-bold underline" data-testid="profile-incomplete-cta">{t("premium.complete_profile_cta")}</Link>
          </p>
        )}
        <button data-testid="checkout-pay-button" onClick={pay} disabled={busy || !waiver} className="btn-volt w-full disabled:opacity-50">
          {busy ? t("common.loading") : t("checkout.pay")}
        </button>
        <button
          type="button"
          data-testid="checkout-free-button"
          onClick={() => navigate("/watchlist")}
          className="w-full rounded-full border border-[#1F293D] px-4 py-2.5 font-mono2 text-[11px] font-bold uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00FF87]/60 hover:text-[#00FF87]"
        >
          {t("checkout.free_instead")}
        </button>
      </section>
    </div>
  );
}
